import React from 'react';
import { Box, Container, Typography, Grid, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import logo from "../../assets/images/lmsLogo.png";

// Styled Components
const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  padding: theme.spacing(10, 0),
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 0),
  },
}));

const ContentGrid = styled(Grid)(({ theme }) => ({
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(4),
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  color: '#f7c42d',
  letterSpacing: '2px',
  marginBottom: theme.spacing(1),
  textTransform: 'uppercase',
  fontFamily: 'Hind Mysuru, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 800,
  color: '#0e2b51',
  marginBottom: theme.spacing(3),
  lineHeight: 1.2,
  fontFamily: 'Hind Mysuru, sans-serif',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
  },
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  lineHeight: 1.8,
  color: '#4a4a4a',
  marginBottom: theme.spacing(2.5),
  fontFamily: 'Hind Mysuru, sans-serif',
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
    lineHeight: 1.7,
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  height: 'auto',
  filter: 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.1))',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '300px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '250px',
  },
}));

const HighlightSection = styled(Box)(({ theme }) => ({
  backgroundColor: 'linear-gradient(135deg, #f7c42d 0%, #e5b327 100%)',
  padding: theme.spacing(6, 4),
  borderRadius: '24px',
  textAlign: 'center',
  boxShadow: '0 8px 32px rgba(247, 196, 45, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #0e2b51 0%, #1a3d6b 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, transparent 0%, #f7c42d 50%, transparent 100%)',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4, 3),
    borderRadius: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2),
  },
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  fontSize: '2.2rem',
  fontWeight: 800,
  color: '#ffffff',
  lineHeight: 1.4,
  fontFamily: 'Hind Mysuru, sans-serif',
  position: 'relative',
  '& span': {
    color: '#f7c42d',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-4px',
      left: 0,
      right: 0,
      height: '3px',
      background: '#f7c42d',
      borderRadius: '2px',
    },
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1.8rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem',
    lineHeight: 1.5,
  },
}));

const StatsGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(4),
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  border: '2px solid transparent',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: '#f7c42d',
    boxShadow: '0 8px 30px rgba(247, 196, 45, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StatIconWrapper = styled(Box)(({ theme }) => ({
  width: '70px',
  height: '70px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  '& img': {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
  },
  [theme.breakpoints.down('sm')]: {
    width: '60px',
    height: '60px',
    '& img': {
      width: '50px',
      height: '50px',
    },
  },
}));

const StatTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#0e2b51',
  marginBottom: theme.spacing(1),
  fontFamily: 'Hind Mysuru, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const StatDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: '#6a6a6a',
  fontFamily: 'Hind Mysuru, sans-serif',
  fontWeight: 500,
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
  },
}));

// Stats data
const statsData = [
  {
    icon: <img src="/mortarboard.gif" alt="Upskilling" />,
    title: "Upskilling",
    description: "Enhance existing skills to stay competitive"
  },
  {
    icon: <img src="/growth.gif" alt="Reskilling" />,
    title: "Reskilling",
    description: "Learn new skills for career transitions"
  },
  {
    icon: <img src="/team.gif" alt="Youth Empowerment" />,
    title: "Youth Empowerment",
    description: "Building capable and confident workforce"
  },
  {
    icon: <img src="/construction-gear.gif" alt="Industry Ready" />,
    title: "Industry Ready",
    description: "Practical training for real-world success"
  }
];

const AboutUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SectionContainer>
      <Container maxWidth="lg">
        {/* Main Content Section */}
        <ContentGrid container spacing={4}>
          {/* Text Content */}
          <Grid item xs={12} md={7}>
            <Label>About Us</Label>
            <MainHeading>What is SkillServe?</MainHeading>
            <Paragraph>
              SkillServe is an innovative learning platform developed by Amass Skill Ventures, dedicated to empowering individuals through practical and digital skill development. With a focus on upskilling, reskilling, and skilling manpower.
            </Paragraph>
            <Paragraph>
              SkillServe aims to meet the evolving demands of various industries by equipping the workforce with the right tools and knowledge. The platform is committed to youth empowerment, offering a wide range of programs such as capacity-building sessions, industry-specific courses, and advanced skill enhancement training.
            </Paragraph>
          </Grid>

          {/* Logo */}
          <Grid item xs={12} md={5}>
            <LogoContainer>
              <LogoImage src={logo} alt="SkillServe Logo" />
            </LogoContainer>
          </Grid>
        </ContentGrid>

        {/* Stats Section */}
        <StatsGrid container spacing={3}>
          {statsData.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatCard>
                <StatIconWrapper>
                  {stat.icon}
                </StatIconWrapper>
                <StatTitle>{stat.title}</StatTitle>
                <StatDescription>{stat.description}</StatDescription>
              </StatCard>
            </Grid>
          ))}
        </StatsGrid>

        {/* Highlight Message */}
        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <HighlightSection>
            <HighlightText>
              <span>SkillServe</span> is your trusted partner in achieving success and a brighter future!
            </HighlightText>
          </HighlightSection>
        </Box>
      </Container>
    </SectionContainer>
  );
};

export default AboutUs;